/* eslint-disable no-alert */
import { useRef, useEffect, ReactElement, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual as _isEqual, debounce } from 'lodash'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { toast, cssTransition } from 'react-toastify'
import { PlanType, PricingPlans } from '@adalo/constants'

import Page from 'components/Shared/Page'
import usePlanCards from 'components/Shared/PlanSelectModal/usePlanCards'
import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'
import Loading from 'components/Shared/Loading'

import {
  fetchTCIOrganizations,
  getOrganizations,
  getPagination,
  getLoading,
  updateTCIOrganizationSubscription,
} from 'ducks/tci'
import { getPricingPlans } from 'ducks/billing'
import { adaloBackendAxios } from 'utils/io/http/axios'

import { formatDate } from 'utils/datetime'
import { getPlanName } from 'utils/billing'
import { Organization } from './types'

import '../Admin/Admin.scss'
import './styles.scss'

interface ItemParams {
  organization: Organization
  pricingPlans: PricingPlans
}

const OrganizationItem = ({
  organization,
  pricingPlans,
}: ItemParams): ReactElement => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isUpdating, setIsUpdating] = useState(false)
  const orgStatus = organization.active ? 'paying' : 'free'
  const [user] = organization?.OrganizationUsers
  const admin = user?.User

  const currentPlanName =
    getPlanName(organization.planType, pricingPlans) || 'Free'

  const planOptions = usePlanCards(organization.planType)

  const handlePlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const plan = e.target.value as PlanType
    const active = plan !== 'free'

    setIsUpdating(true)

    dispatch(updateTCIOrganizationSubscription(organization.id, plan, active))

    setTimeout(() => {
      setIsUpdating(false)

      const planDisplayName = getPlanName(plan, pricingPlans) || 'Free'

      toast(
        <>
          <div>
            <h2>{t('Plan Updated Successfully')}</h2>
            <p>
              {organization.name} {t('has been updated to')} {planDisplayName}{' '}
              {t('plan')}
            </p>
          </div>
        </>,
        {
          position: 'top-right',
          className: 'tci-update__toast tci-update__toast--success',
          bodyClassName: 'tci-update__toast-body',
          hideProgressBar: true,
          autoClose: 5000,
          transition: cssTransition({
            enter: 'animate__animated animate__fadeInUp',
            exit: 'animate__animated animate__fadeOutDown',
            collapse: false,
          }),
        }
      )
    }, 1000)
  }

  return (
    <div className="admin-user-item">
      <Link to={`/tci/organizations/${organization.id}`}>
        {organization.name}
      </Link>
      <p
        style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        <span
          className={classNames('admin-org-status', `admin-org-${orgStatus}`)}
        >
          {t(currentPlanName)}
        </span>
      </p>
      <p>{admin?.email}</p>
      <p>{formatDate(organization.createdAt)}</p>
      <p>{formatDate(organization.updatedAt)}</p>
      <td>
        <select
          onChange={handlePlanChange}
          value={organization.planType || ''}
          style={{ minWidth: 120 }}
          disabled={isUpdating}
        >
          {isUpdating && (
            <option value="" disabled>
              Updating...
            </option>
          )}
          {planOptions.order.map(plan => {
            const { displayName, planType } =
              planOptions.cards[plan as keyof typeof planOptions.cards] || {}

            return (
              <option key={planType} value={planType}>
                {displayName}
              </option>
            )
          })}
        </select>
      </td>
    </div>
  )
}

const TCIOrganizations = (): ReactElement => {
  const mounted = useRef<boolean>(false)
  const dispatch = useDispatch()
  const organizations = useSelector(state => getOrganizations(state), _isEqual)
  const pagination = useSelector(getPagination)
  const loading = useSelector(getLoading)
  const pricingPlans = useSelector(getPricingPlans) as PricingPlans
  const { t } = useTranslation()

  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [exporting, setExporting] = useState(false)
  const PAGE_SIZE = 50

  const totalPages = Math.ceil(pagination.total / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1
  const endIndex = Math.min(currentPage * PAGE_SIZE, pagination.total)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      const offset = (page - 1) * PAGE_SIZE
      dispatch(fetchTCIOrganizations(offset, PAGE_SIZE, searchTerm))
    }
  }

  const handleSearch = useCallback(
    (search: string) => {
      setSearchTerm(search)
      setCurrentPage(1) // Reset to first page when searching
      dispatch(fetchTCIOrganizations(0, PAGE_SIZE, search))
    },
    [dispatch]
  )

  const debouncedSearch = useCallback(
    debounce((search: string) => {
      handleSearch(search)
    }, 1000),
    [handleSearch]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleExport = async () => {
    try {
      setExporting(true)

      // Call the export endpoint
      const response = await adaloBackendAxios.get<string>(
        '/tci/organizations/export'
      )

      const csvFileData = response.data
      const contentType = 'text/csv'

      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss')
      const filterContext = searchTerm ? `_filtered-by-${searchTerm}` : ''
      const filename = `tci-organizations_${timestamp}${filterContext}.csv`

      // Create a hidden download link and click it.
      const blob = new Blob([csvFileData], { type: contentType })
      const url = URL.createObjectURL(blob)
      const hiddenElement = document.createElement('a')
      hiddenElement.href = url
      hiddenElement.download = filename
      hiddenElement.click()
      URL.revokeObjectURL(url)

      toast.success(t('Export completed successfully'), {
        position: 'bottom-right',
      })
    } catch (error) {
      console.error('TCI CSV Export Failed:', error)
      toast.error(t('Export failed. Please try again.'), {
        position: 'bottom-right',
      })
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    dispatch(fetchTCIOrganizations(0, PAGE_SIZE, ''))
    mounted.current = true
  }, [])

  return (
    <Page className="admin-users-page" title={t('Organizations')}>
      <section className="users-header">
        <h1>{t('Organizations')}</h1>
        <div className="organizations-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder={t('Search organizations...')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <Icon
              type="search"
              className="search-icon"
              small
              color="darkerGray"
            />
          </div>
          <div className="organizations-actions">
            <Button
              small
              onClick={handleExport}
              disabled={exporting}
              icon="download"
            >
              {exporting ? t('Exporting...') : t('Export CSV')}
            </Button>
          </div>
          <div className="organizations-stats">
            <span>
              {t('Showing')} {startIndex}-{endIndex} {t('of')}{' '}
              {pagination.total} {t('organizations')}
            </span>
          </div>
        </div>
      </section>
      <div className="admin-users-header">
        <p>{t('Name')}</p>
        <p
          style={{
            maxWidth: 120,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t('Current Plan')}
        </p>
        <p>{t('Owner')}</p>
        <p>{t('Created At')}</p>
        <p>{t('Updated At')}</p>
        <p>{t('Plan Selector')}</p>
      </div>

      {loading ? (
        <div className="organizations-loading">
          <Loading />
          <p>{t('Loading organizations...')}</p>
        </div>
      ) : (
        organizations.map(org => (
          <OrganizationItem
            key={org.id}
            organization={org}
            pricingPlans={pricingPlans}
          />
        ))
      )}

      {totalPages > 1 && (
        <div className="simple-pagination">
          <div className="pagination-info">
            {t('Page')} {currentPage} {t('of')} {totalPages}
          </div>
          <div className="pagination-controls">
            <Button
              small
              disabled={currentPage === 1}
              onClick={() => handlePageChange(1)}
            >
              {t('First Page')}
            </Button>

            <Button
              small
              text
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <Icon type="arrow-back" small />
            </Button>

            <span className="page-numbers">
              {currentPage} / {totalPages}
            </span>

            <Button
              small
              text
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <Icon type="arrow-next" small />
            </Button>

            <Button
              small
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(totalPages)}
            >
              {t('Last Page')}
            </Button>
          </div>
        </div>
      )}
    </Page>
  )
}

export default TCIOrganizations
